import Joi from "joi";
import i18n from "../i18n";

export { validateEmail } from "./emailValidation";
export type { EmailValidationResult } from "./emailValidation";
export { validatePhone } from "./phoneValidation";
export type { PhoneValidationResult } from "./phoneValidation";

export const createShema = (order: any, bypass?: boolean) => {
  if (!order) return;
  let objextToreturn: any = {};
  order.forEach((element: any) => {
    if (element.field_type !== "comments") {
      if (element.field_type === "input-number") {
        objextToreturn[element.uuid] = element.required
          ? Joi.number().required().messages({
              "number.base": i18n.t("orderingStatus.fieldMustBeNumber"),
              "any.required": i18n.t("orderingStatus.fieldMustBeNumber"),
            })
          : Joi.any();
      } else {
        if (element.field_type !== "additionSelect") {
          objextToreturn[element.uuid] = element.required
            ? Joi.any()
                .empty("")
                .required()
                .messages({ "any.required": i18n.t("orderingStatus.fieldRequired") })
            : Joi.any();
        } else {
          objextToreturn[element.uuid] =
            element?.minValue > 0
              ? Joi.array()
                  .min(element?.minValue ?? 1)
                  .messages({ "any.required": i18n.t("orderingStatus.fieldRequired") })
              : Joi.any();
        }
      }
    } else {
      objextToreturn[element.uuid] = Joi.any();
    }
  });
  return Joi.object(objextToreturn);
};

export const joiResolver = (schema: any) => {
  return (data: any) => {
    const { error, value } = schema.validate(data, { abortEarly: false });

    return {
      values: error ? {} : value,
      errors: error
        ? error.details.reduce((previous: any, currentError: any) => {
            return {
              ...previous,
              [currentError.path.join(".")]: {
                type: currentError.type,
                message: currentError.message,
              },
            };
          }, {})
        : {},
    };
  };
};

export const createFormInitialValue = (order: any) => {
  if (!order) return;
  let objextToreturn: any = {};
  order.forEach((element: any) => {
    if (element.field_type === "addition") {
      objextToreturn[`${element.uuid}`] = element.value ?? 0;
      return;
    }
    if (element.field_type === "additionSelect") {
      objextToreturn[`${element.uuid}`] = [];
      return;
    } else {
      if (element.field_type !== "google-maps-api") {
        objextToreturn[`${element.uuid}`] =
          element?.getFromStorage && localStorage.getItem(element.uuid)
            ? localStorage.getItem(element.uuid)
            : element.value ?? undefined;
        return;
      } else {
        objextToreturn[`${element.uuid}`] = undefined;
        return;
      }
    }
  });
  return objextToreturn;
};

export const saveiInStorage = (data: any) => {
  if (!data) return;
  Object.keys(data).forEach((key) => {
    if (data[key]) {
      if (typeof data[key] === "object" && data[key]?.place_id) {
        localStorage.setItem(key, JSON.stringify(data[key].place_id));
      } else localStorage.setItem(key, data[key]);
    }
  });
};

export const scrollToElementById = (id: any, scrollElementId?: string) => {
  const element = document.getElementById(id);
  if (element) {
    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - 35;

    if (scrollElementId) {
      const e = document.getElementById(scrollElementId);
      e?.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  }
};

export const scrollToPosition = (offsetPosition: number) => {
  window.scrollTo({
    top: offsetPosition,
    behavior: "auto",
  });
};

export const createDaysArray = () => {
  const daysOfWeek = [
    "niedziela",
    "poniedziałek",
    "wtorek",
    "środa",
    "czwartek",
    "piątek",
    "sobota",
  ];
  const monthsShort = [
    "Sty",
    "Lut",
    "Mar",
    "Kwi",
    "Maj",
    "Cze",
    "Lip",
    "Sie",
    "Wrz",
    "Paź",
    "Lis",
    "Gru",
  ];
  const result = [];

  for (let i = 0; i < 90; i++) {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + i);

    let dayName = daysOfWeek[currentDate.getDay()];
    if (i === 0) {
      dayName = "dziś";
    } else if (i === 1) {
      dayName = "jutro";
    }

    const shortDateName = `${currentDate.getDate()} ${
      monthsShort[currentDate.getMonth()]
    }`;

    result.push({
      date: currentDate.toLocaleDateString("pl-PL"),
      dayName: dayName,
      shortDateName: shortDateName,
    });
  }

  return result;
};

type TimeRange = { from: string; till: string };
type Schedule = { [key: string]: TimeRange[] };

const parseTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

export const isCurrentTimeInSchedule = (schedule: Schedule): boolean => {
  const now = new Date();
  const currentDay = now
    .toLocaleString("en-US", { weekday: "long" })
    .toLowerCase();
  const currentTime = now.getHours() * 60 + now.getMinutes(); // Konwersja na minuty

  if (!schedule[currentDay]) return false; // Brak przedziałów czasowych dla dnia

  return schedule[currentDay].some(({ from, till }) => {
    const fromMinutes = parseTimeToMinutes(from);
    const tillMinutes = parseTimeToMinutes(till);
    return currentTime >= fromMinutes && currentTime <= tillMinutes;
  });
};

export const ucfirst = (str: string | null | undefined) => {
  if (!str) return "";
  var firstLetter = str.slice(0, 1);
  return firstLetter.toUpperCase() + str.substring(1);
};

export const getNextOpeningTime = (schedule: Schedule): { time: string; day: string; relativeDay: string } | null => {
  if (!schedule) return null;

  const dayOrder = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const dayNamesPolish: { [key: string]: string } = {
    monday: "poniedziałek",
    tuesday: "wtorek",
    wednesday: "środę",
    thursday: "czwartek",
    friday: "piątek",
    saturday: "sobotę",
    sunday: "niedzielę",
  };

  const now = new Date();
  const currentDayIndex = now.getDay();
  const currentTime = now.getHours() * 60 + now.getMinutes();

  // Sprawdź dzisiejsze godziny otwarcia (czy jest jeszcze jakaś godzina otwarcia dzisiaj)
  const currentDayName = dayOrder[currentDayIndex];
  const todaySchedule = schedule[currentDayName] || [];

  for (const range of todaySchedule) {
    const fromMinutes = parseTimeToMinutes(range.from);
    if (currentTime < fromMinutes) {
      // Jest jeszcze godzina otwarcia dzisiaj
      return {
        time: range.from,
        day: dayNamesPolish[currentDayName],
        relativeDay: "today"
      };
    }
  }

  // Sprawdź następne 7 dni
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7;
    const nextDayName = dayOrder[nextDayIndex];
    const nextDaySchedule = schedule[nextDayName] || [];

    if (nextDaySchedule.length > 0) {
      const firstOpeningTime = nextDaySchedule[0].from;
      const relativeDay = i === 1 ? "tomorrow" : "later";

      return {
        time: firstOpeningTime,
        day: dayNamesPolish[nextDayName],
        relativeDay
      };
    }
  }

  return null;
};

export const getBranchOrderingStatus = (
  isOpen: boolean,
  schedule: Schedule
): {
  status: "active" | "closing-soon" | "closed";
  color: "green" | "yellow" | "red";
  message: string;
} => {
  if (!isOpen) {
    const nextOpening = getNextOpeningTime(schedule);
    let message = i18n.t("orderingStatus.onlineOrderingUnavailable");

    if (nextOpening) {
      if (nextOpening.relativeDay === "today") {
        message = i18n.t("orderingStatus.backTodayAt", { time: nextOpening.time });
      } else if (nextOpening.relativeDay === "tomorrow") {
        message = i18n.t("orderingStatus.backTomorrowAt", { time: nextOpening.time });
      } else {
        message = i18n.t("orderingStatus.backOnDayAt", { day: nextOpening.day, time: nextOpening.time });
      }
    }

    return {
      status: "closed",
      color: "red",
      message,
    };
  }

  // Sprawdź czy zamknięcie jest za mniej niż 1h
  const now = new Date();
  const currentDayIndex = now.getDay();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  const dayOrder = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const currentDayName = dayOrder[currentDayIndex];
  const todaySchedule = schedule[currentDayName] || [];

  for (const range of todaySchedule) {
    const fromMinutes = parseTimeToMinutes(range.from);
    const tillMinutes = parseTimeToMinutes(range.till);

    if (currentTime >= fromMinutes && currentTime <= tillMinutes) {
      const timeUntilClosing = tillMinutes - currentTime;

      if (timeUntilClosing <= 60) {
        return {
          status: "closing-soon",
          color: "yellow",
          message: i18n.t("orderingStatus.encourageOrderingClosingSoon", { till: range.till }),
        };
      }

      return {
        status: "active",
        color: "green",
        message: i18n.t("orderingStatus.encourageOrderingUntil", { till: range.till }),
      };
    }
  }

  return {
    status: "active",
    color: "green",
    message: i18n.t("orderingStatus.encourageOrdering"),
  };
};
